class BookFinder extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.steps = [...this.querySelectorAll('[data-book-finder-step]')];
    this.activeSteps = [];
    this.currentStep = 0;
    this.indexPromise = null;

    this.querySelector('[data-book-finder-open]')?.addEventListener('click', () => this.open());
    this.querySelector('[data-book-finder-close]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-book-finder-back]')?.addEventListener('click', () => this.back());
    this.querySelector('[data-book-finder-next]')?.addEventListener('click', () => this.next());
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog?.addEventListener('close', () => document.documentElement.classList.remove('book-finder-open'));
    this.querySelectorAll('input').forEach((input) => input.addEventListener('change', () => this.update()));
  }

  open() {
    this.currentStep = 0;
    this.querySelector('form')?.reset();
    if (typeof this.dialog?.showModal === 'function') this.dialog.showModal();
    else this.dialog?.setAttribute('open', '');
    this.dialog?.focus({ preventScroll: true });
    document.documentElement.classList.add('book-finder-open');
    this.update();
  }

  close() {
    if (typeof this.dialog?.close === 'function') this.dialog.close();
    else this.dialog?.removeAttribute('open');
    document.documentElement.classList.remove('book-finder-open');
  }

  back() {
    if (this.currentStep === 0) {
      this.close();
      return;
    }
    this.currentStep -= 1;
    this.update();
  }

  next() {
    if (!this.isCurrentStepValid()) return;
    if (this.currentStep < this.activeSteps.length - 1) {
      this.currentStep += 1;
      this.update();
      return;
    }
    this.submitFilters();
  }

  isCurrentStepValid() {
    const current = this.activeSteps[this.currentStep];
    const isOptionalChildGenres = current?.dataset.bookFinderAudience === 'bambini'
      && current.dataset.bookFinderKind === 'genres';

    if (isOptionalChildGenres) {
      current.removeAttribute('data-invalid');
      return true;
    }

    const checked = current?.querySelectorAll('input:checked').length > 0;
    current?.toggleAttribute('data-invalid', !checked);
    if (!checked) {
      this.querySelector('.book-finder__error')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    return checked;
  }

  update() {
    const audience = this.selectedValue('quiz_audience') || 'adulti';
    const skipAdultPurpose = audience === 'adulti' && this.selectedValue('quiz_reader_profile') === 'regalo';
    const adultGiftPurpose = this.querySelector('input[name="quiz_purpose"][value="regalo"]');

    if (skipAdultPurpose && adultGiftPurpose) {
      adultGiftPurpose.checked = true;
      adultGiftPurpose.dataset.bookFinderAutoSelected = 'true';
    } else if (adultGiftPurpose?.dataset.bookFinderAutoSelected === 'true') {
      adultGiftPurpose.checked = false;
      delete adultGiftPurpose.dataset.bookFinderAutoSelected;
    }

    this.activeSteps = this.steps.filter((step) => {
      if (step.dataset.bookFinderAudience && step.dataset.bookFinderAudience !== audience) return false;
      if (skipAdultPurpose && step.dataset.bookFinderAudience === 'adulti' && step.dataset.bookFinderKind === 'purpose') return false;
      return true;
    });
    this.currentStep = Math.min(this.currentStep, Math.max(this.activeSteps.length - 1, 0));
    const current = this.activeSteps[this.currentStep];
    this.steps.forEach((step) => step.toggleAttribute('hidden', step !== current));
    const progress = this.querySelector('[data-book-finder-progress]');
    const stepNumber = this.querySelector('[data-book-finder-step-number]');
    const counter = this.querySelector('[data-book-finder-counter]');
    const next = this.querySelector('[data-book-finder-next]');
    const error = this.querySelector('.book-finder__error');
    if (progress) progress.style.setProperty('--progress', `${((this.currentStep + 1) / this.activeSteps.length) * 100}%`);
    if (stepNumber) stepNumber.textContent = String(this.currentStep + 1);
    if (counter) counter.textContent = `${this.currentStep + 1} di ${this.activeSteps.length}`;
    if (next) next.querySelector('span').textContent = this.currentStep === this.activeSteps.length - 1 ? 'Scopri i libri per te!' : 'Avanti';
    if (error) error.textContent = current?.dataset.bookFinderKind === 'genres' ? 'Seleziona almeno una categoria' : 'Seleziona almeno una risposta';
    this.dialog?.style.setProperty('--book-finder-height', `${current?.dataset.bookFinderHeight || 650}px`);
    current?.removeAttribute('data-invalid');
  }

  normalize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('it')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  async loadIndexPage(page) {
    const url = new URL(this.dataset.indexUrl || '/collections/all?view=book-finder-index', window.location.origin);
    url.searchParams.set('page', String(page));

    const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Book finder index request failed: ${response.status}`);
    return response.json();
  }

  loadIndex() {
    if (this.indexPromise) return this.indexPromise;

    this.indexPromise = (async () => {
      const products = [];
      let page = 1;
      let totalPages = 1;

      do {
        const payload = await this.loadIndexPage(page);
        products.push(...(Array.isArray(payload.products) ? payload.products : []));
        totalPages = Number(payload.pages) || 1;
        page += 1;
      } while (page <= totalPages);

      return products;
    })().catch((error) => {
      this.indexPromise = null;
      throw error;
    });

    return this.indexPromise;
  }

  selectedValue(name) {
    return this.querySelector(`input[name="${name}"]:checked`)?.value || '';
  }

  selectedValues(name) {
    return [...this.querySelectorAll(`input[name="${name}"]:checked`)].map((input) => input.value);
  }

  matchesProfile(productValue, selectedValue) {
    const aliases = {
      vorace: ['lettore-vorace-piu-di-12-libri-anno', 'non-riesce-a-stare-senza-libro-in-mano'],
      affezionato: ['lettore-regolare-4-12-libri-anno', 'legge-quando-puo-ma-ci-tiene-molto'],
      ripartenza: ['sto-riscoprendo-il-piacere-di-leggere', 'stiamo-cercando-di-farlo-leggere-di-piu'],
      regalo: ['e-un-regalo-per-qualcun-altro'],
      incuriosire: ['non-e-un-gran-fan-vogliamo-incuriosirlo'],
      prelettore: ['e-ancora-troppo-piccolo-per-leggere-da-solo'],
      curioso: ['e-un-bambino-molto-curioso'],
    };
    const normalizedValue = this.normalize(productValue);
    return (aliases[selectedValue] || [selectedValue]).some(
      (alias) => normalizedValue === alias || normalizedValue.includes(alias)
    );
  }

  matchesGenre(product, selectedGenres) {
    const collectionAliases = {
      'libri-contemporanei': ['libri-contemporanei', 'contemporanei', 'narrativa'],
      'gialli-thriller': ['gialli-thriller', 'thriller'],
      'libri-storici': ['libri-storici', 'romanzi-storici', 'storici'],
      'storie-amore': ['storie-amore', 'rosa-romance', 'romance'],
      'libri-saggistica': ['libri-saggistica', 'saggistica-memoir', 'saggistica-e-memoir', 'saggistica'],
      cartonati: ['cartonati'],
      'libri-da-lettura': ['libri-da-lettura'],
      'colorati-stickers': ['colorati-stickers', 'colorati-e-stickers', 'libri-da-colorare'],
      prescolari: ['prescolari'],
    };
    const handles = Array.isArray(product.collections) ? product.collections.map((value) => this.normalize(value)) : [];
    return selectedGenres.some((genre) =>
      (collectionAliases[genre] || [genre]).some((alias) => handles.includes(alias))
    );
  }

  matchesAge(productValue, selectedValues) {
    const aliases = {
      '0-2': ['0-2', '0-2-anni'],
      '3-5': ['3-5', '3-5-anni'],
      '5-7': ['5-7', '5-7-anni'],
      '8-9': ['8-9', '8-9-anni'],
      '10-plus': ['10', '10-anni', '10-plus', '10-anni-in-su'],
    };
    const normalizedValues = (Array.isArray(productValue) ? productValue : [productValue]).map((value) => this.normalize(value));
    return selectedValues.some((selectedValue) =>
      (aliases[selectedValue] || [selectedValue]).some((alias) =>
        normalizedValues.some((value) => value === alias || value.includes(alias))
      )
    );
  }

  isCurated(product, selectedPath) {
    if (selectedPath === 'catalogo') return true;
    return Boolean(product.curated?.[selectedPath]);
  }

  stableHash(value) {
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  }

  selectMatches(products, filters) {
    const audienceProducts = products.filter(
      (product) => this.normalize(product.audience) === filters.audience
    );
    if (!audienceProducts.length) return [];

    const genreMatches = filters.genres.length
      ? audienceProducts.filter((product) => this.matchesGenre(product, filters.genres))
      : audienceProducts;
    const hasProfileData = audienceProducts.some((product) => this.normalize(product.readerProfile));
    const hasAgeData = audienceProducts.some((product) => {
      const values = Array.isArray(product.age) ? product.age : [product.age];
      return values.some((value) => this.normalize(value));
    });
    const hasGenreData = filters.audience === 'adulti' || audienceProducts.some((product) =>
      ['cartonati', 'libri-da-lettura', 'colorati-stickers', 'colorati-e-stickers', 'libri-da-colorare', 'prescolari']
        .some((handle) => (product.collections || []).map((value) => this.normalize(value)).includes(handle))
    );
    let candidates = hasGenreData && filters.genres.length ? genreMatches : audienceProducts;

    const shouldFilterProfile = hasProfileData && !(
      filters.audience === 'adulti' && filters.readerProfile === 'regalo'
    );

    if (shouldFilterProfile) {
      candidates = candidates.filter((product) => this.matchesProfile(product.readerProfile, filters.readerProfile));
    }
    if (filters.audience === 'bambini' && hasAgeData) {
      candidates = candidates.filter((product) => this.matchesAge(product.age, filters.childAges));
    }
    if (filters.audience === 'adulti' && filters.curatedPath !== 'catalogo') {
      candidates = candidates.filter((product) => this.isCurated(product, filters.curatedPath));
    }

    const seed = [
      filters.audience,
      filters.readerProfile,
      ...filters.genres.slice().sort(),
      filters.curatedPath,
      ...filters.childAges.slice().sort(),
    ].join('|');

    return candidates
      .map((product) => ({ product, tieBreaker: this.stableHash(`${seed}|${product.id}`) }))
      .sort((left, right) => left.tieBreaker - right.tieBreaker)
      .map((entry) => entry.product);
  }

  buildSearchQuery(products) {
    return products
      .map((product) => String(product.title || '').trim())
      .filter(Boolean)
      .filter((title, index, titles) => titles.indexOf(title) === index)
      .map((title) => `title:${JSON.stringify(title)}`)
      .join(' OR ');
  }

  async submitFilters() {
    const nextButton = this.querySelector('[data-book-finder-next]');
    const nextLabel = nextButton?.querySelector('span');
    const error = this.querySelector('.book-finder__error');
    const originalLabel = nextLabel?.textContent;

    if (nextButton) nextButton.disabled = true;
    if (nextLabel) nextLabel.textContent = 'Cerco…';
    if (error) error.textContent = '';

    try {
      const audience = this.selectedValue('quiz_audience');
      const readerProfile = audience === 'bambini'
        ? this.selectedValue('quiz_child_reader_profile')
        : this.selectedValue('quiz_reader_profile');
      const genres = audience === 'bambini' ? this.selectedValues('quiz_child_genres') : this.selectedValues('quiz_genres');
      const curatedPath = this.selectedValue('quiz_curated_path');
      const childAges = this.selectedValues('quiz_child_age');
      const products = await this.loadIndex();
      const matches = this.selectMatches(products, {
        audience,
        readerProfile,
        genres,
        curatedPath,
        childAges,
      });

      const url = new URL(this.dataset.resultsUrl || '/search', window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'none');
      url.searchParams.set('q', this.buildSearchQuery(matches) || '__wizard_no_results__');
      url.searchParams.set('book_finder_audience', audience);
      if (matches.length) {
        url.searchParams.set('book_finder_ids', matches.map((product) => product.id).join(','));
      }
      window.location.assign(url.toString());
    } catch (errorValue) {
      console.error('Unable to load book finder results', errorValue);
      if (error) error.textContent = 'Non riusciamo a caricare i risultati. Riprova tra poco.';
      if (nextButton) nextButton.disabled = false;
      if (nextLabel) nextLabel.textContent = originalLabel || 'Scopri i libri per te!';
    }
  }
}

if (!customElements.get('book-finder')) customElements.define('book-finder', BookFinder);
